import { useCallback, useEffect, useState } from 'react';
import { XrayLogStreamRtcConnection, XrayRtcConnectionInfo } from '@bymason/controller-api';
import { GetRtcConnectionHook } from '@bymason/api-hooks-base';

/**
 * Based on enum here
 * https://gitlab.com/masonamerica/android/mason-core-apps/-/blob/varis/xray/app/core/src/main/java/com/bymason/platform/core/xray/actions/request/LogLevel.java
 */
export type LogLevel = 'debug' | 'error' | 'fatal' | 'info' | 'verbose' | 'warn';
export type XrayLogStreamConnectionInfo = XrayRtcConnectionInfo;

/**
 * React hook that manages an X-Ray Log-Stream connection.
 * @returns HookArray
 * @HookArray [0] start: method that starts the connection
 * @HookArray [1] stop: method that stops the connection
 * @HookArray [2] dataChannel: connection's RTC data channel (or null)
 * @HookArray [3] connectionInfo: current connection info (or null)
 * @HookArray [4] timedOut: flag that indicates whether or not connection timed out
 * @HookArray [5] error: string (empty if there's no error)
 * @HookArray [6] busy: flag that indicates whether or not connection is in-process of starting
 */
export function useXrayLogStreamConnection(deviceId: string, clientToken: string): GetRtcConnectionHook {
  const [connection, setConnection] = useState<XrayLogStreamRtcConnection | null>(null);
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [connectionInfo, setConnectionInfo] = useState<XrayLogStreamConnectionInfo | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleDataChannelOpen = useCallback((dc: RTCDataChannel) => {
    setDataChannel(dc);
  }, []);

  const handleError = useCallback((err: string) => {
    setError(err);
  }, []);

  const handleConnectionInfo = useCallback((info: XrayLogStreamConnectionInfo) => {
    setConnectionInfo(info);
  }, []);

  const handleTimeout = useCallback(() => {
    setTimedOut(true);
  }, []);

  const start = useCallback(
    (params?: URLSearchParams) => {
      setConnectionInfo(null);
      setDataChannel(null);
      setError('');
      setTimedOut(false);

      connection?.start(params);
    },
    [connection]
  );

  const stop = useCallback(() => {
    connection?.stop();
  }, [connection]);

  useEffect(() => {
    setBusy(connectionInfo?.status === 'pending');
  }, [connectionInfo?.status]);

  useEffect(() => {
    if (clientToken) {
      const newConnection = new XrayLogStreamRtcConnection({
        clientToken,
        deviceId,
        onDataChannelOpen: handleDataChannelOpen,
        onError: handleError,
        onReport: handleConnectionInfo,
        onConnectionStop: handleConnectionInfo,
        onTimeout: handleTimeout,
      });

      setConnection(newConnection);
    }
  }, [clientToken, deviceId, handleConnectionInfo, handleDataChannelOpen, handleError, handleTimeout]);

  return [start, stop, dataChannel, connectionInfo, timedOut, error, busy];
}
